import React, { useState } from 'react'
import { Droppable } from 'react-beautiful-dnd'
import {
  Box,
  Typography,
  TextField,
  IconButton,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import IdeaCard from './IdeaCard'
import { roadmapAPI } from './api/roadmapAPI'
import { toast } from 'react-toastify'
import { useTranslation } from 'react-i18next' // LAP-733 (GE022)

// All categories for Horizon 10 plan (Famille and Domicile are distinct labels; both use backend domain HOME)
// LAP-733 (GE022): label/prompt resolved via t() at render time
const DOMAIN_CATEGORIES = [
  { value: 'PROFESSIONAL', labelKey: 'catProfessional', promptKey: 'promptProfessional' },
  { value: 'FINANCE',      labelKey: 'catFinance',      promptKey: 'promptFinance' },
  { value: 'HEALTH',       labelKey: 'catHealth',       promptKey: 'promptHealth' },
  { value: 'SOCIAL',       labelKey: 'catSocial',       promptKey: 'promptSocial' },
  { value: 'HOME',         labelKey: 'catHome',         promptKey: 'promptHome' },
  { value: 'FOOTPRINT',    labelKey: 'catFootprint',    promptKey: 'promptFootprint' },
  { value: 'PERSONAL',     labelKey: 'catPersonal',     promptKey: 'promptPersonal' },
  { value: 'HOME',         labelKey: 'catHomeEnv',      promptKey: 'promptHomeEnv' },
  { value: 'ADMIN',        labelKey: 'catAdmin',        promptKey: 'promptAdmin' },
  { value: 'TIME',         labelKey: 'catTime',         promptKey: 'promptTime' },
  { value: 'SPORTS',       labelKey: 'catSports',       promptKey: 'promptSports' },
  { value: 'LEISURE',      labelKey: 'catLeisure',      promptKey: 'promptLeisure' },
  { value: 'SPIRITUAL',    labelKey: 'catSpiritual',    promptKey: 'promptSpiritual' },
  { value: 'OTHER',        labelKey: 'catOther',        promptKey: 'promptOther' },
]

const Horizon10Section = ({ currentYear, ideas = [], onIdeaClick, onUpdate }) => {
  const { t } = useTranslation('layout') // LAP-733 (GE022)
  const [editingDomain, setEditingDomain] = useState(null)
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')

  const handleAddClick = (domain) => {
    setEditingDomain({ ...domain, uniqueKey: `${domain.value}-${domain.labelKey}` })
    setTitle('')
    setDescription('')
  }

  const handleCancel = () => {
    setEditingDomain(null)
    setTitle('')
    setDescription('')
  }

  const handleSave = async (domain) => {
    if (!title.trim()) {
      toast.error(t('roadmap.toastTitleRequired'))
      return
    }

    try {
      await roadmapAPI.createIdea({
        title: title.trim(),
        description: description.trim() || '',
        domain: domain.value,
        idea_type: 'ACCOMPLISH', // Default type for 10-year horizon
        horizon: 'YEAR_10',
      })
      
      toast.success(t('roadmap.toastIdeaCreated'))
      setEditingDomain(null)
      setTitle('')
      setDescription('')
      
      if (onUpdate) {
        onUpdate()
      }
    } catch (error) {
      toast.error(t('roadmap.toastErrIdeaCreate'))
      console.error(error)
    }
  }

  // Group ideas by domain (backend value)
  const ideasByDomain = ideas.reduce((acc, idea) => {
    const domain = idea.domain || 'OTHER'
    if (!acc[domain]) {
      acc[domain] = []
    }
    acc[domain].push(idea)
    return acc
  }, {})

  // Use DOMAIN_CATEGORIES as the single source of categories so all are shown (e.g. Famille + Domicile)
  const displayCategories = DOMAIN_CATEGORIES

  return (
    <Accordion
      defaultExpanded={false}
      sx={{
        mb: 3,
        backgroundColor: 'rgba(40, 40, 40, 0.9)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        borderRadius: '12px',
        backdropFilter: 'blur(10px)',
        boxShadow: 'none',
        '&:before': {
          display: 'none',
        },
        '& .MuiAccordionSummary-root': {
          minHeight: 64,
          padding: '0 24px',
          '&.Mui-expanded': {
            minHeight: 64,
          },
        },
        '& .MuiAccordionSummary-content': {
          margin: '16px 0',
          '&.Mui-expanded': {
            margin: '16px 0',
          },
        },
        '& .MuiAccordionDetails-root': {
          padding: '0 24px 24px 24px',
        },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon sx={{ color: 'white' }} />}
        sx={{
          '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
            transform: 'rotate(180deg)',
          },
        }}
      >
        <Box>
        <Typography
          variant="h6"
          sx={{
            color: 'white',
            fontWeight: 600,
            mb: 1,
            fontSize: { xs: '1.2rem', md: '1.4rem' },
          }}
        >
          {t('roadmap.horizon10Header', { year: currentYear + 10 })} 🧭
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: 'rgba(255, 255, 255, 0.85)',
            lineHeight: 1.75,
            fontSize: { xs: '0.9rem', md: '1rem' },
          }}
        >
          {t('roadmap.horizon10Intro')}
        </Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <Droppable droppableId="year_10">
          {(provided, snapshot) => (
            <Box
              ref={provided.innerRef}
              {...provided.droppableProps}
              sx={{
                minHeight: snapshot.isDraggingOver ? '100px' : '50px',
                backgroundColor: snapshot.isDraggingOver
                  ? 'rgba(25, 118, 210, 0.1)'
                  : 'transparent',
                borderRadius: '8px',
                p: snapshot.isDraggingOver ? 1 : 0,
                transition: 'all 0.2s ease',
              }}
            >
            {displayCategories.map((domain, index) => {
              const domainIdeas = ideasByDomain[domain.value] || []
              const isEditing = editingDomain?.value === domain.value && editingDomain?.labelKey === domain.labelKey

              return (
                <Box key={`${domain.value}-${domain.labelKey}-${index}`} sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        color: 'white',
                        fontWeight: 600,
                        flex: 1,
                        fontSize: { xs: '1rem', md: '1.1rem' },
                      }}
                    >
                      {t('roadmap.' + domain.labelKey)} :
                    </Typography>
                    {!isEditing && (
                      <IconButton
                        size="small"
                        onClick={() => handleAddClick(domain)}
                        sx={{ color: 'white' }}
                      >
                        <AddIcon />
                      </IconButton>
                    )}
                  </Box>

                  {isEditing ? (
                    <Box
                      sx={{
                        backgroundColor: 'rgba(255, 255, 255, 0.05)',
                        borderRadius: '8px',
                        p: 2,
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                      }}
                    >
                      <TextField
                        fullWidth
                        placeholder={t('roadmap.' + domain.promptKey)}
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        sx={{
                          mb: 2,
                          '& .MuiOutlinedInput-root': {
                            backgroundColor: 'rgba(255, 255, 255, 0.1)',
                            color: 'white',
                            '& fieldset': {
                              borderColor: 'rgba(255, 255, 255, 0.3)',
                            },
                            '&:hover fieldset': {
                              borderColor: 'rgba(255, 255, 255, 0.5)',
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: 'rgba(255, 255, 255, 0.7)',
                            },
                          },
                          '& .MuiInputBase-input::placeholder': {
                            color: 'rgba(255, 255, 255, 0.5)',
                          },
                        }}
                        autoFocus
                      />
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                        <Button
                          size="small"
                          onClick={handleCancel}
                          sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
                        >
                          {t('roadmap.cancel')}
                        </Button>
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={<CheckIcon />}
                          onClick={() => handleSave(domain)}
                          disabled={!title.trim()}
                        >
                          {t('roadmap.add')}
                        </Button>
                      </Box>
                    </Box>
                  ) : (
                    <>
                      {domainIdeas.length > 0 && (
                        <Box
                          sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 1.5,
                            mb: 1.5,
                          }}
                        >
                          {domainIdeas.map((idea, idx) => (
                            <IdeaCard
                              key={idea.id}
                              idea={idea}
                              index={idx}
                              onClick={onIdeaClick}
                              horizon="YEAR_10"
                            />
                          ))}
                        </Box>
                      )}
                      {domainIdeas.length === 0 && (
                        <Box
                          sx={{
                            backgroundColor: 'rgba(255, 255, 255, 0.05)',
                            borderRadius: '8px',
                            p: 2,
                            border: '1px dashed rgba(255, 255, 255, 0.2)',
                            textAlign: 'center',
                            color: 'rgba(255, 255, 255, 0.5)',
                            cursor: 'pointer',
                            '&:hover': {
                              backgroundColor: 'rgba(255, 255, 255, 0.08)',
                            },
                          }}
                          onClick={() => handleAddClick(domain)}
                        >
                          <Typography variant="body2">
                            {t('roadmap.' + domain.promptKey)}
                          </Typography>
                        </Box>
                      )}
                    </>
                  )}
                </Box>
              )
            })}
            {provided.placeholder}
            </Box>
          )}
        </Droppable>
      </AccordionDetails>
    </Accordion>
  )
}

export default Horizon10Section

